import { forwardRef } from 'react';

import { Box, CircularProgress, Stack } from '@mui/material';
import { ChatMessageContent, type ChatMessageContentProps } from '@mui/x-chat';
import { useMessageContext } from '@mui/x-chat/headless';

import { Icon } from '@/components/atoms/Icon/Icon';
import {
  BARE_BUBBLE_CLASS,
  FULL_WIDTH_BUBBLE_CLASS,
} from '@/theme/mui/components';

// Parts that draw their own surface, so the bubble around them is left bare.
const ATTACHMENT_PARTS = [
  'data-file',
  'data-voice',
  'data-card',
  'data-menu',
  'data-activity',
];

// Parts that run the full width of the thread rather than a bubble's share:
// what a turn did is measured against the thread, not against its own words,
// so a line doesn't wrap only until something lands beside it.
const FULL_WIDTH_PARTS = ['data-card', 'data-activity'];

const isOnly = (parts: { type: string }[], types: string[]) =>
  parts.length > 0 && parts.every((part) => types.includes(part.type));

const SPINNER_PX = 14;

// Muted while it is read.
const readingSx = { opacity: 0.6 };

const besideBubbleSx = { alignItems: 'center', justifyContent: 'flex-end' };

/**
 * AttachmentMessageContent leaves the bubble bare when the parts draw their
 * own, flags a failed send beside it, and mutes one still being read, with a
 * spinner beside it.
 */
export const AttachmentMessageContent = forwardRef<
  HTMLDivElement,
  ChatMessageContentProps
>((props, ref) => {
  const { message, role, status } = useMessageContext();
  const isBare = !!message && isOnly(message.parts, ATTACHMENT_PARTS);
  const isFullWidth = !!message && isOnly(message.parts, FULL_WIDTH_PARTS);
  // Our own mark on the message, which the chat's metadata type does not list.
  const isBeingRead =
    role === 'user' &&
    !!(message?.metadata as { isBeingRead?: boolean })?.isBeingRead;
  const bubble = {
    ...(isBare && {
      className: isFullWidth
        ? `${BARE_BUBBLE_CLASS} ${FULL_WIDTH_BUBBLE_CLASS}`
        : BARE_BUBBLE_CLASS,
    }),
    ...(isBeingRead && { sx: readingSx, 'aria-busy': true }),
  };
  const content = (
    <ChatMessageContent
      ref={ref}
      {...props}
      slotProps={isBare || isBeingRead ? { bubble } : undefined}
    />
  );

  if (status === 'error' && role === 'user') {
    return (
      <Stack direction='row' spacing={1} sx={besideBubbleSx}>
        <Box sx={{ color: 'error.main', display: 'flex', flexShrink: 0 }}>
          <Icon icon='exclamation-circle' fontSize={16} />
        </Box>
        {content}
      </Stack>
    );
  }

  if (isBeingRead) {
    return (
      <Stack direction='row' spacing={1} sx={besideBubbleSx}>
        <Box
          sx={{ color: 'custom.neutral.600', display: 'flex', flexShrink: 0 }}
        >
          <CircularProgress size={SPINNER_PX} color='inherit' />
        </Box>
        {content}
      </Stack>
    );
  }

  return content;
});

AttachmentMessageContent.displayName = 'AttachmentMessageContent';
export default AttachmentMessageContent;
